import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { convertTransactionAmounts } from '@/lib/currencyConversion/currencyConversion';
import { fetchRatesWithRetry } from '@/integrations/api/currency';

export interface TransferInput {
  sourceAccountId: string;
  targetAccountId: string;
  amount: number;
  fee?: number;
  currency: string;
  transactionDate?: string;
  description?: string;
  notes?: string;
}

interface TransferResponse {
  source_transaction_id: string;
  target_transaction_id: string;
  transfer_id: string;
}

/**
 * Hook to create inter-account transfer transactions
 * Creates paired transactions atomically via RPC function
 * Invalidates transactions and accounts queries on success
 */
export function useCreateTransfer() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: TransferInput): Promise<TransferResponse> => {
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Validate source and target accounts are different
      if (input.sourceAccountId === input.targetAccountId) {
        throw new Error('Source and target accounts must be different');
      }

      // Validate amount is positive
      if (input.amount <= 0) {
        throw new Error('Transfer amount must be greater than zero');
      }

      // Validate fee is non-negative if provided
      if (input.fee !== undefined && input.fee < 0) {
        throw new Error('Transfer fee cannot be negative');
      }

      // Fetch source and target account native currencies
      const { data: accounts, error: accountsError } = await supabase
        .from('accounts')
        .select('id, currency')
        .in('id', [input.sourceAccountId, input.targetAccountId])
        .eq('user_id', user.id);

      if (accountsError || !accounts || accounts.length < 2) {
        throw new Error('One or both accounts not found');
      }

      const sourceAccount = accounts.find((a) => a.id === input.sourceAccountId);
      const targetAccount = accounts.find((a) => a.id === input.targetAccountId);

      if (!sourceAccount || !targetAccount) {
        throw new Error('One or both accounts not found');
      }

      const sourceCurrency = sourceAccount.currency;
      const targetCurrency = targetAccount.currency;

      // Convert source leg (amount + fee) from transaction currency to source account currency
      const sourceResult = await convertTransactionAmounts(
        {
          amount: input.amount,
          fee: input.fee ?? 0,
          fromCurrency: input.currency,
          toCurrency: sourceCurrency,
        },
        fetchRatesWithRetry
      );

      // Convert target leg (amount only, no fee) from transaction currency to target account currency
      const targetResult = await convertTransactionAmounts(
        {
          amount: input.amount,
          fee: 0,
          fromCurrency: input.currency,
          toCurrency: targetCurrency,
        },
        fetchRatesWithRetry
      );

      // Call the create_transfer_transaction RPC function with per-leg converted amounts
      const { data, error } = await supabase.rpc('create_transfer_transaction', {
        p_user_id: user.id,
        p_source_account_id: input.sourceAccountId,
        p_target_account_id: input.targetAccountId,
        p_amount: input.amount,
        p_fee: input.fee ?? 0,
        p_currency: input.currency,
        p_transaction_date: input.transactionDate ?? new Date().toISOString(),
        p_description: input.description ?? null,
        p_notes: input.notes ?? null,
        p_source_amount: sourceResult.convertedAmount,
        p_source_fee: sourceResult.convertedFee,
        p_target_amount: targetResult.convertedAmount,
        p_source_currency: sourceCurrency,
        p_target_currency: targetCurrency,
      } as any);

      if (error) {
        // Handle specific error cases
        if (error.message?.includes('same account')) {
          throw new Error('Cannot transfer to the same account');
        }
        if (error.message?.includes('insufficient balance')) {
          throw new Error('Insufficient balance in source account');
        }
        if (error.message?.includes('not found')) {
          throw new Error('One or both accounts not found');
        }
        
        // Generic error
        throw new Error(error.message || 'Failed to create transfer');
      }

      if (!data) {
        throw new Error('No data returned from transfer creation');
      }

      return data as unknown as TransferResponse;
    },
    onSuccess: () => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: (error: Error) => {
      console.error('Transfer creation failed:', error);
    },
  });
}
